/**
 * SigningStepper — 4-step visual indicator for the customer wallet flow.
 *
 * Steps:
 *   1 · Dati          → customer fills anagrafica
 *   2 · Privacy       → GDPR preferences + privacy PDF read
 *   3 · Contratto     → contract PDF read + OTP signature
 *   4 · Card          → digital wallet card + Apple/Google Pass
 *
 * The stepper stays visible across all 4 steps for continuous UX orientation
 * (Mara/Zorzutti feedback 24/07/2026: previously only step 3 showed a 3-dot
 * stepper — the Privacy step was invisible).
 *
 * On the Contratto step it also shows a live "contract read %" progress bar,
 * fed by `usePdfReadConfirmation` in ContractModal via `readingProgressPct`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';

const STEP_KEYS = ['data', 'privacy', 'contract', 'card'];
// Sample retention window (ms) for the read-speed rolling average.
const ETA_WINDOW_MS = 5000;

export const SigningStepper = ({
  currentStep,           // 1 | 2 | 3 | 4
  readingProgressPct,    // 0-100 (only used on currentStep === 3)
  translations = {},
}) => {
  const labels = {
    data:     translations.progress_data     || 'Dati',
    privacy:  translations.progress_privacy  || 'Privacy',
    contract: translations.progress_contract || 'Contratto',
    card:     translations.progress_card     || 'Card',
  };
  const readLabel = translations.progress_read_pct || 'del contratto letto';
  const stepOfLabel = translations.progress_step_of || 'Step';
  const readingCompleteLabel =
    translations.reading_complete_toast ||
    'Lettura completata — ora puoi firmare';
  const etaMinLabel = translations.eta_min_remaining || 'min rimanenti';
  const etaSecLabel = translations.eta_sec_remaining || 'sec rimanenti';
  const etaAlmostLabel = translations.eta_almost_done || 'quasi finito';
  const totalSteps = STEP_KEYS.length;
  const activeLabel = labels[STEP_KEYS[currentStep - 1]] || '';
  const showProgress = currentStep === 3 && typeof readingProgressPct === 'number';
  const pct = Math.max(0, Math.min(100, Math.round(readingProgressPct || 0)));
  const isReadingComplete = showProgress && pct >= 100;

  // ETA calculation — rolling average of the last ETA_WINDOW_MS samples.
  // The customer sees a friendly "~2 min rimanenti" hint next to the
  // read-% badge (Fabrizio req. 24/07/2026): reduces abandonment by
  // impatient customers who otherwise don't know how much is left.
  const samplesRef = useRef([]);
  const [etaSec, setEtaSec] = useState(null);
  useEffect(() => {
    if (!showProgress || isReadingComplete) {
      samplesRef.current = [];
      setEtaSec(null);
      return;
    }
    const now = Date.now();
    samplesRef.current.push({ t: now, pct });
    // Drop samples older than the rolling window.
    samplesRef.current = samplesRef.current.filter(s => now - s.t <= ETA_WINDOW_MS);
    if (samplesRef.current.length < 2) {
      setEtaSec(null);
      return;
    }
    const first = samplesRef.current[0];
    const last = samplesRef.current[samplesRef.current.length - 1];
    const dPct = last.pct - first.pct;
    const dTsec = (last.t - first.t) / 1000;
    if (dPct <= 0 || dTsec <= 0) {
      setEtaSec(null); // no forward progress → hide the ETA gracefully
      return;
    }
    const velocity = dPct / dTsec; // % per second
    const remainingSec = Math.max(1, Math.round((100 - pct) / velocity));
    // Cap absurd values (customer scrolls one pixel every 30s → ETA=1h+)
    // to avoid alarming the user. Anything over 30 min we simply hide.
    setEtaSec(remainingSec > 1800 ? null : remainingSec);
  }, [pct, showProgress, isReadingComplete]);

  const formatEta = (sec) => {
    if (sec == null) return null;
    if (sec < 10) return etaAlmostLabel;
    if (sec < 60) return `~${sec} ${etaSecLabel}`;
    return `~${Math.round(sec / 60)} ${etaMinLabel}`;
  };
  const etaText = formatEta(etaSec);

  // Emit a success toast ONCE when the customer reaches 100% reading.
  // The ref guards against duplicate toasts on re-renders or when the
  // user re-scrolls the last section (Fabrizio req. 24/07/2026).
  const toastFiredRef = useRef(false);
  useEffect(() => {
    if (isReadingComplete && !toastFiredRef.current) {
      toastFiredRef.current = true;
      toast.success(readingCompleteLabel, {
        duration: 3500,
        id: 'contract-reading-complete',
        icon: '✓',
      });
    }
    // Reset the guard when we leave step 3 so the toast can fire again
    // on a fresh signing session (edge case: back → forward navigation).
    if (currentStep !== 3) {
      toastFiredRef.current = false;
    }
  }, [isReadingComplete, currentStep, readingCompleteLabel]);

  return (
    <div className="mb-8" data-testid="signing-stepper">
      {/* Step title + counter */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-lojack-red text-xs uppercase tracking-widest font-semibold">
            {stepOfLabel} {currentStep} / {totalSteps}
          </span>
          <span
            className="text-white text-lg font-bold truncate"
            data-testid="signing-stepper-title"
          >
            {activeLabel}
          </span>
        </div>
        {showProgress && (
          <div className="flex items-center gap-2 shrink-0">
            {etaText && !isReadingComplete && (
              <span
                className="text-gray-400 text-[10px] font-mono tabular-nums hidden sm:inline"
                data-testid="signing-stepper-eta"
                title="Tempo stimato di lettura rimanente (calcolato sulla velocità degli ultimi 5 secondi)"
              >
                {etaText}
              </span>
            )}
            <span
              className={`text-xs font-mono tabular-nums transition-colors duration-300 ${
                isReadingComplete
                  ? 'text-emerald-400 font-semibold'
                  : 'text-lojack-red'
              }`}
              data-testid="signing-stepper-pct"
              data-reading-complete={isReadingComplete ? 'true' : 'false'}
            >
              {isReadingComplete ? '✓ ' : ''}{pct}% {readLabel}
            </span>
          </div>
        )}
      </div>

      {/* 4 dots + 3 connectors */}
      <div className="flex items-center justify-center gap-1 sm:gap-2">
        {STEP_KEYS.map((k, idx) => {
          const stepNum = idx + 1;
          const isCompleted = stepNum < currentStep;
          const isActive = stepNum === currentStep;
          const dotClass = isCompleted
            ? 'bg-lojack-blue-600 text-white'
            : isActive
            ? 'bg-lojack-red text-white ring-4 ring-lojack-red/20'
            : 'bg-gray-700 text-gray-400';
          const labelClass = isCompleted
            ? 'text-lojack-blue-400'
            : isActive
            ? 'text-lojack-red font-semibold'
            : 'text-gray-500';
          const connectorClass = isCompleted ? 'bg-lojack-blue-600' : 'bg-gray-600';
          return (
            <React.Fragment key={k}>
              <div className="flex items-center gap-1.5" data-testid={`signing-stepper-step-${stepNum}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${dotClass}`}>
                  {isCompleted ? <Check size={16} strokeWidth={3} /> : stepNum}
                </div>
                <span className={`text-xs hidden sm:inline ${labelClass}`}>
                  {labels[k]}
                </span>
              </div>
              {idx < totalSteps - 1 && (
                <div className={`h-px flex-1 max-w-8 sm:max-w-12 ${connectorClass}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Reading progress bar — only on Contratto step.
          At 100% the bar turns emerald green with a soft pulse to
          signal that the "Firma" button is now unlocked (Fabrizio
          req. 24/07/2026). */}
      {showProgress && (
        <div
          className={`mt-3 mx-2 h-1.5 rounded-full overflow-hidden transition-colors duration-500 ${
            isReadingComplete
              ? 'bg-emerald-900/40 ring-2 ring-emerald-400/50 animate-pulse'
              : 'bg-gray-700'
          }`}
          data-testid="signing-stepper-progress-bar"
          data-reading-complete={isReadingComplete ? 'true' : 'false'}
        >
          <div
            className={`h-full transition-all duration-300 ease-out ${
              isReadingComplete
                ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-[0_0_12px_rgba(52,211,153,0.6)]'
                : 'bg-gradient-to-r from-lojack-red to-lojack-red/70'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default SigningStepper;
